import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth.service';

declare global {
  interface Window {
    __mkvmRealtime?: {
      url?: string;
      state?: string;
      topics?: UISubscriptionTopic[];
      lastMessage?: any;
      lastUiEvent?: any;
      lastSubscribePayload?: any;
    };
  }
}

export interface UISubscriptionTopic {
  topic: 'notifications' | 'chat.thread' | 'chat.thread_list' | 'property.status' | 'property.detail' | 'user.profile' | 'presence';
  thread_id?: string;
  property_id?: string;
  user_id?: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private ws: WebSocket | null = null;
  private reconnectTimer: any = null;
  private reconnectDelay = 1000;

  readonly messages$ = new Subject<any>();
  readonly notifications$ = new Subject<any>();
  readonly threads$ = new Subject<any>();
  readonly presence$ = new Subject<any>();
  readonly connection$ = new Subject<'connected' | 'disconnected'>();
  readonly uiEvents$ = new Subject<any>();
  private topics = new Map<string, UISubscriptionTopic>();

  constructor(private auth: AuthService) {}

  private decodeTokenExp(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return typeof payload?.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  private async ensureFreshToken(): Promise<string | null> {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    const exp = this.decodeTokenExp(token);
    const now = Math.floor(Date.now() / 1000);
    if (!exp || exp - now > 30) {
      return token;
    }

    try {
      const refreshed = await this.auth.refreshTokens();
      return refreshed?.access_token || localStorage.getItem('access_token');
    } catch {
      return null;
    }
  }

  connect(): void {
    if (typeof window === 'undefined') return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    window.__mkvmRealtime = window.__mkvmRealtime || {};

    void (async () => {
      const token = await this.ensureFreshToken();
      if (!token) {
        console.log('[ws] no token, connecting anonymously');
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const query = token ? `?token=${encodeURIComponent(token)}` : '';
      const wsUrl = `${protocol}//${window.location.host}/api/ws${query}`;
      window.__mkvmRealtime!.url = wsUrl;
      window.__mkvmRealtime!.state = 'connecting';
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[ws] connected', wsUrl);
        window.__mkvmRealtime!.state = 'open';
        this.reconnectDelay = 1000;
        this.connection$.next('connected');
        this.resubscribe();
      };

      this.ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          console.log('[ws] message', data?.type, data?.payload);
          window.__mkvmRealtime!.lastMessage = data;
          if (data.type === 'ui.ready') {
            this.resubscribe();
            return;
          }
          if (data.type === 'ui.event') {
            this.handleUiEvent(data.event);
            return;
          }
          if (data.type === 'new_message') this.messages$.next(data.payload);
          if (data.type === 'notification_created') this.notifications$.next(data.payload);
          if (data.type === 'thread_updated') this.threads$.next(data.payload);
          if (data.type === 'presence_update') this.presence$.next(data.payload);
        } catch {}
      };

      this.ws.onclose = () => {
        console.log('[ws] disconnected');
        window.__mkvmRealtime!.state = 'closed';
        this.connection$.next('disconnected');
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        console.log('[ws] error');
        window.__mkvmRealtime!.state = 'error';
        this.connection$.next('disconnected');
        this.scheduleReconnect();
      };
    })();
  }

  disconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.ws?.close();
    this.ws = null;
  }

  subscribeTopics(topics: UISubscriptionTopic[]): void {
    for (const topic of topics) {
      this.topics.set(this.topicKey(topic), topic);
    }
    const payload = { type: 'subscribe', topics };
    if (typeof window !== 'undefined') {
      window.__mkvmRealtime = window.__mkvmRealtime || {};
      window.__mkvmRealtime.topics = Array.from(this.topics.values());
      window.__mkvmRealtime.lastSubscribePayload = payload;
    }
    console.log('[ws] subscribe', payload);
    this.send(payload);
  }

  unsubscribeTopics(topics: UISubscriptionTopic[]): void {
    for (const topic of topics) {
      this.topics.delete(this.topicKey(topic));
    }
    if (typeof window !== 'undefined') {
      window.__mkvmRealtime = window.__mkvmRealtime || {};
      window.__mkvmRealtime.topics = Array.from(this.topics.values());
    }
    console.log('[ws] unsubscribe', topics);
    this.send({ type: 'unsubscribe', topics });
  }

  private resubscribe(): void {
    const topics = Array.from(this.topics.values());
    if (typeof window !== 'undefined') {
      window.__mkvmRealtime = window.__mkvmRealtime || {};
      window.__mkvmRealtime.topics = topics;
    }
    if (topics.length) {
      const payload = { type: 'subscribe', topics };
      if (typeof window !== 'undefined') {
        window.__mkvmRealtime!.lastSubscribePayload = payload;
      }
      console.log('[ws] resubscribe', payload);
      this.send(payload);
    }
  }

  private send(payload: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(payload));
  }

  private handleUiEvent(event: any): void {
    if (typeof window !== 'undefined') {
      window.__mkvmRealtime = window.__mkvmRealtime || {};
      window.__mkvmRealtime.lastUiEvent = event;
    }
    console.log('[ws] ui.event', event?.name, event?.scope, event?.changed_fields);
    this.uiEvents$.next(event);
    if (event?.name === 'ui.chat.message.created') {
      this.messages$.next(event.payload);
      this.threads$.next(event);
      return;
    }
    if (event?.name === 'ui.chat.thread.changed') {
      this.threads$.next(event);
      return;
    }
    if (event?.name === 'ui.property.changed' || event?.name === 'ui.property.status.changed' || event?.name === 'ui.property.photo_ai_observation.updated') {
      this.uiEvents$.next(event);
      return;
    }
    if (event?.name === 'ui.notifications.changed') {
      if (event.payload?.id) {
        this.notifications$.next(event.payload);
      } else {
        this.notifications$.next(event);
      }
      return;
    }
    if (event?.name === 'ui.presence.changed') {
      this.presence$.next(event.payload || event.scope);
    }
  }

  private topicKey(topic: UISubscriptionTopic): string {
    return [topic.topic, topic.thread_id || '', topic.property_id || '', topic.user_id || ''].join('|');
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15000);
    }, this.reconnectDelay);
  }
}
